import { Popup } from '@progress/kendo-react-popup'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import getConfig from 'next/config'
import { useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { createChargebeeCustomer } from 'services/chargebee'
import { Tenant } from 'types/tenant'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import {
    formatCreditCardNumber,
    formatCVC,
    formatExpirationDate,
    validateExpirationDate,
} from './CardUtils'
import { AddCardFormValues, CardApi, CreateCardProps } from './types'

const { CHARGEBEE_ACCOUNT_ID } = getConfig().publicRuntimeConfig

function CreateCard({ handleSaveSuccess, translations }: CreateCardProps) {
    const { setGlobalError } = useAppErrorApiContext()
    const { setGlobalLoading } = useAppContext()
    const { user_profile, CreditCardModal } = useAuth()
    const { tenant, setTenant } = useTenantContext()
    const { close: closeModal } = CreditCardModal
    const [show, setShow] = useState(false)
    const ref = useRef<HTMLElement>(null)

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<AddCardFormValues>({
        mode: 'onChange',
    })

    const onSubmit: SubmitHandler<AddCardFormValues> = async (
        values: Record<string, string>
    ) => {
        setGlobalLoading(true)

        const { first_name, last_name, cardNumber, expirydate, cvcCvv } =
            values
        const expirydate_arr = expirydate.split('/')
        const new_card: CardApi = {
            cardGatewayAccountId: CHARGEBEE_ACCOUNT_ID,
            cardFirstName: first_name,
            cardLastName: last_name,
            cardNumber: cardNumber,
            cardExpiryMonth: expirydate_arr[0],
            cardExpiryYear: expirydate_arr[1],
            cardCvv: cvcCvv,
        }

        let api = null
        let customer_id = tenant?.chargebeeCustomerId
        if (!customer_id) {
            api = await createChargebeeCustomer(
                tenant,
                user_profile,
                new_card
            )
            if (api.ok) {
                setTenant({
                    ...tenant,
                    chargebeeCustomerId: api.id,
                } as Tenant)
            }
        } else {
            api = await postApiRequest(
                `/v1/chargebee/payment-sources/card?customerId=${customer_id}`,
                new_card
            )
        }

        if (api && api.ok) {
            handleSaveSuccess(new_card, api.card.brand)
        } else {
            setGlobalError(api)
        }

        setGlobalLoading(false)
    }

    return (
        <div
            className={classNames(
                'bg-white rounded-2xl shadow-2xl p-5 w-full xl:w-3/4 m-auto'
            )}
        >
            <Translation
                className="flex justify-center text-xl"
                content_key="add_card"
                render_as="div"
            />
            <div>
                <form
                    method="POST"
                    onSubmit={handleSubmit(onSubmit)}
                    className="z-20 mt-5"
                >
                    <fieldset className="flex gap-3 pb-3">
                        <div className="w-1/2">
                            <label
                                htmlFor="card-holder"
                                className={classNames(
                                    'block font-bold text-gray-600',
                                    errors.first_name?.type
                                        ? 'text-red-700'
                                        : ''
                                )}
                            >
                                <Translation content_key="first_name" />
                            </label>
                            <input
                                className={classNames(
                                    'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                    errors.first_name
                                        ? 'border-red-300 bg-red-100'
                                        : ''
                                )}
                                type="text"
                                placeholder={getTranslation(
                                    'enter_first_name',
                                    translations
                                )}
                                {...register('first_name', {
                                    required: getTranslation(
                                        'first_name_required',
                                        translations
                                    ),
                                })}
                            />
                            {errors.first_name && (
                                <span className="text-sm text-red-700">
                                    {errors.first_name.message}
                                </span>
                            )}
                        </div>
                        <div className="w-1/2">
                            <label
                                htmlFor="card-holder"
                                className={classNames(
                                    'block font-bold text-gray-600',
                                    errors.last_name?.type
                                        ? 'text-red-700'
                                        : ''
                                )}
                            >
                                <Translation content_key="last_name" />
                            </label>
                            <input
                                className={classNames(
                                    'mt-1 focus:ring-primary-light focus:border-primary-light block w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                    errors.last_name?.type
                                        ? 'border-red-300 bg-red-100'
                                        : ''
                                )}
                                type="text"
                                placeholder={getTranslation(
                                    'enter_last_name',
                                    translations
                                )}
                                {...register('last_name', {
                                    required: getTranslation(
                                        'last_name_required',
                                        translations
                                    ),
                                })}
                            />
                            {errors.last_name && (
                                <span className="text-sm text-red-700">
                                    {errors.last_name.message}
                                </span>
                            )}
                        </div>
                    </fieldset>
                    <fieldset className="flex gap-3 pb-3">
                    <div className="w-full">
                            <label
                                htmlFor="card-holder"
                                className={classNames(
                                    'block font-bold text-gray-600',
                                    errors.cardNumber ? 'text-red-700' : ''
                                )}
                            >
                                <Translation content_key="card_number" />
                            </label>
                            <div className="relative">
                                <input
                                    className={classNames(
                                        'mt-1 focus:ring-primary-light focus:border-primary-light w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                        errors.cardNumber
                                            ? 'border-red-300 bg-red-100'
                                            : ''
                                    )}
                                    type="text"
                                    placeholder="0000 0000 0000 0000"
                                    {...register('cardNumber', {
                                        required: getTranslation(
                                            'card_number_required',
                                            translations
                                        ),
                                        onChange: (e) =>
                                            setValue(
                                                'cardNumber',
                                                formatCreditCardNumber(
                                                    e.target.value
                                                )
                                            ),
                                    })}
                                />
                                <i className="feather-credit-card absolute right-4 top-1 text-xl text-primary-light" />
                            </div>
                            {errors.cardNumber && (
                                <span className="text-sm text-red-700">
                                    {errors.cardNumber.message}
                                </span>
                            )}
                        </div>
                        
                    </fieldset>

                    <fieldset className="flex gap-3 pb-3">
                    <div className="w-1/2">
                        <label
                                htmlFor="card-holder"
                                className={classNames(
                                    'block font-bold text-gray-600',
                                    errors.expirydate ? 'text-red-700' : ''
                                )}
                            >
                                <Translation content_key="expiry_date" />
                            </label>
                            <div className="relative">
                                <input
                                    className={classNames(
                                        'mt-1 focus:ring-primary-light focus:border-primary-light w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                        errors.expirydate
                                            ? 'border-red-300 bg-red-100'
                                            : ''
                                    )}
                                    type="text"
                                    placeholder="MM/YYYY"
                                    {...register('expirydate', {
                                        required: getTranslation(
                                            'expiry_date_required',
                                            translations
                                        ),
                                        validate: (v) =>
                                            validateExpirationDate(v) ||
                                            getTranslation(
                                                'invalid_expiry_date',
                                                translations
                                            ),
                                        onChange: (e) =>
                                            setValue(
                                                'expirydate',
                                                formatExpirationDate(
                                                    e.target.value
                                                )
                                            ),
                                    })}
                                />
                                <i className="feather-calendar absolute right-4 top-1 text-xl text-primary-light" />
                            </div>
                            {errors.expirydate && (
                                <span className="text-sm text-red-700">
                                    {errors.expirydate.message}
                                </span>
                            )}
                        </div>
                    <div className="w-1/2">
                        <label
                                htmlFor="cvc-cvv"
                                className={classNames(
                                    'block font-bold text-gray-600',
                                    errors.cvcCvv ? 'text-red-700' : ''
                                )}
                            >
                                <Translation content_key="cvc_cvv" />
                            </label>
                            <div className="relative">
                                <input
                                    className={classNames(
                                        'mt-1 focus:ring-primary-light focus:border-primary-light w-full shadow-sm sm:text-sm border-gray-300 rounded-md',
                                        errors.cvcCvv
                                            ? 'border-red-300 bg-red-100'
                                            : ''
                                    )}
                                    type="password"
                                    placeholder="***"
                                    {...register('cvcCvv', {
                                        required: getTranslation(
                                            'cvc_cvv_required',
                                            translations
                                        ),
                                        pattern: {
                                            value: /\d{3,4}/,
                                            message: getTranslation(
                                                'cvc_cvv_warning',
                                                translations
                                            ),
                                        },
                                        onChange: (e) =>
                                            setValue(
                                                'cvcCvv',
                                                formatCVC(e.target.value)
                                            ),
                                    })}
                                />
                                <i className="feather-help-circle absolute right-4 top-1 text-xl text-primary-light"
                                    ref={ref}
                                    onMouseOver={() => {
                                        setShow(true)
                                    }}
                                    onMouseOut={() => {
                                        setShow(false)
                                    }}
                                />
                            </div>
                            {errors.cvcCvv && (
                                <span className="text-sm text-red-700">
                                    {errors.cvcCvv.message}
                                </span>
                            )}
                        </div>
                    </fieldset>

                    <Popup
                        show={show}
                        animate={false}
                        anchorAlign={{
                            horizontal: 'right',
                            vertical: 'bottom',
                        }}
                        popupAlign={{
                            horizontal: 'left',
                            vertical: 'bottom',
                        }}
                        anchor={ref.current}
                        popupClass={
                            'bg-black text-white text-xs shadow-lg rounded-xl w-40 px-2 py-1 mt-2'
                        }
                        className="z-[102]"
                    >
                        <div className="rounded" style={{ overflow: 'hidden' }}>
                            <Translation
                                render_as="span"
                                content_key="accounts.cvv" />
                        </div>
                    </Popup>
                    <div className="flex justify-end gap-3 mt-5 w-full">
                        <button
                            type="button"
                            className={classNames(
                                'group relative flex justify-center',
                                'py-1 px-2 border border-gray-300',
                                'focus:outline-none text-md rounded-md'
                            )}
                            onClick={closeModal}
                        >
                            <Translation content_key="cancel" />
                        </button>
                        <button
                            type="submit"
                            className={classNames(
                                'group relative flex justify-center',
                                'py-1 px-2 border border-transparent',
                                'text-md rounded-md text-white',
                                'focus:outline-none',
                                'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                            )}
                            onClick={handleSubmit(onSubmit)}
                        >
                            <Translation content_key="add_card" />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default CreateCard
